import { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Pages } from '../../../Pages'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { AuthHelper } from '../../../auth/AuthHelper'
import { consumeInviteTarget } from '../../../auth/inviteTarget'
import { useAuth } from '../../../auth/AuthContext'
import authApi from '../../../api/auth/AuthApi'
import CandidateMagicLinkNotification from './CandidateMagicLinkNotification'
import CandidateEmailLinkButton from './CandidateEmailLinkButton'
import {
    MAGIC_LINK_ERROR_FLAG,
    MAGIC_LINK_ERROR_MESSAGE,
} from '../../login/constants'
import { executeRecaptcha } from '../../../utils/recaptcha'
import {
    speakToKitSerifStyle,
    speakToKitThemeVars,
} from '../theme/speakToKitThemeVars'
import SpeakToKitLogo from '../components/SpeakToKitLogo'
import useSpeakToKitPageTitle from '../hooks/useSpeakToKitPageTitle'

enum LoginStep {
    Email = 'email',
    ChooseMethod = 'choose-method',
    NoAccount = 'no-account',
    MagicSent = 'magic-sent',
}

const baseButtonClasses =
    'w-full rounded-2xl px-5 py-3 text-base font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50 disabled:cursor-not-allowed'
const primaryButtonClasses = `${baseButtonClasses} bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90`
const secondaryButtonClasses = `${baseButtonClasses} border border-border bg-transparent text-foreground hover:border-primary/50`

export default function CandidateLogin(): React.JSX.Element {
    useSpeakToKitPageTitle('Log In')
    const { user, loading, error, loginWithPassword, requestMagicLink } =
        useAuth()
    const navigate = useNavigate()
    const [email, setEmail] = useState(() => {
        const params = new URLSearchParams(window.location.search)
        return params.get('email') ?? ''
    })
    const [password, setPassword] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [status, setStatus] = useState<string | null>(() => {
        const params = new URLSearchParams(window.location.search)
        return params.get('magicError') === MAGIC_LINK_ERROR_FLAG
            ? MAGIC_LINK_ERROR_MESSAGE
            : null
    })
    const [missingEmail, setMissingEmail] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [magicLinkMessage, setMagicLinkMessage] = useState<string | null>(
        null,
    )
    const [step, setStep] = useState<LoginStep>(() => {
        const params = new URLSearchParams(window.location.search)
        const stepParam = params.get('step')
        const emailParam = params.get('email')
        if (stepParam === LoginStep.ChooseMethod && emailParam) {
            return LoginStep.ChooseMethod
        }
        return LoginStep.Email
    })

    const rawTargetPath = useMemo(() => {
        const params = new URLSearchParams(window.location.search)
        const raw = params.get('target')
        let source: string | undefined | null = raw
        if (!source) {
            source = consumeInviteTarget()
        }
        if (!source) return undefined
        let decoded: string
        try {
            decoded = decodeURIComponent(source)
        } catch {
            return undefined
        }
        if (!decoded.startsWith('/') || decoded.startsWith('//'))
            return undefined
        return decoded
    }, [])

    const targetPath = useMemo(() => {
        if (!rawTargetPath) return undefined
        const baseOrigin =
            typeof window !== 'undefined' && window.location?.origin
                ? window.location.origin
                : 'http://localhost'
        try {
            const url = new URL(rawTargetPath, baseOrigin)
            if (!url.searchParams.has('returnTo')) {
                return rawTargetPath
            }
            url.searchParams.delete('returnTo')
            const cleaned = `${url.pathname}${url.search}${url.hash}`
            return cleaned || '/'
        } catch {
            return rawTargetPath
        }
    }, [rawTargetPath])

    const editEmailReturnPath = useMemo(() => {
        if (!rawTargetPath) return undefined
        const baseOrigin =
            typeof window !== 'undefined' && window.location?.origin
                ? window.location.origin
                : 'http://localhost'
        try {
            const url = new URL(rawTargetPath, baseOrigin)
            const returnTo = url.searchParams.get('returnTo')
            if (
                !returnTo ||
                !returnTo.startsWith('/') ||
                returnTo.startsWith('//')
            ) {
                return undefined
            }
            return returnTo
        } catch {
            return undefined
        }
    }, [rawTargetPath])

    const autoRedirectHandled = useRef(false)

    useEffect(() => {
        if (step === LoginStep.ChooseMethod && !email) {
            setStep(LoginStep.Email)
        }
    }, [step, email])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        if (!params.has('magicError')) return
        params.delete('magicError')
        const query = params.toString()
        const newUrl = `${window.location.pathname}${
            query ? `?${query}` : ''
        }${window.location.hash}`
        window.history.replaceState(null, '', newUrl)
    }, [])

    useEffect(() => {
        if (!loading && user && !autoRedirectHandled.current) {
            autoRedirectHandled.current = true
            navigate(targetPath || Pages.Roles, { replace: true })
        }
    }, [loading, user, targetPath, navigate])

    const handlePasswordLogin = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()

            if (!email || !password) {
                setStatus('Please provide both email and password.')
                return
            }
            try {
                setSubmitting(true)
                setStatus(null)
                setMissingEmail(null)
                AuthHelper.removeTenantOverrideId()
                const recaptchaToken = await executeRecaptcha('password_login')
                await loginWithPassword(email, password, recaptchaToken)
                navigate(targetPath || Pages.Roles, { replace: true })
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to log in. Please try again.'
                setStatus(
                    message.replace(
                        /^There was an issue communicating with the Contxt server:\s*/,
                        '',
                    ),
                )
                setMissingEmail(extractMissingEmail(message))
            } finally {
                setSubmitting(false)
            }
        },
        [email, password, loginWithPassword, navigate, targetPath],
    )

    const sendMagicLink = useCallback(async () => {
        if (!email) {
            setStatus('Please enter your email to receive a magic link.')
            return
        }
        try {
            setSubmitting(true)
            setMissingEmail(null)
            AuthHelper.removeTenantOverrideId()
            const recaptchaToken = await executeRecaptcha('request_magic_link')
            await requestMagicLink(email, recaptchaToken, targetPath)
            setStep(LoginStep.MagicSent)
            setStatus(null)
            setMagicLinkMessage(`We have sent a magic link to ${email}`)
        } catch (err: unknown) {
            const message =
                err instanceof Error
                    ? err.message
                    : 'Unable to send magic link. Please try again.'
            setStatus(
                message.replace(
                    /^There was an issue communicating with the Contxt server:\s*/,
                    '',
                ),
            )
            setMissingEmail(extractMissingEmail(message))
            setMagicLinkMessage(null)
        } finally {
            setSubmitting(false)
        }
    }, [email, requestMagicLink, targetPath])

    const handleEmailChange = useCallback((value: string) => {
        setEmail(value)
        setMissingEmail(null)
        setStatus(null)
        setMagicLinkMessage(null)
        setPassword('')
    }, [])

    const handleSelectMagicLink = useCallback(() => {
        if (submitting) return
        sendMagicLink()
    }, [sendMagicLink, submitting])

    const handleEmailSubmit = useCallback(
        async (event: React.FormEvent) => {
            event.preventDefault()
            const trimmedEmail = email.trim()
            if (!trimmedEmail) {
                setStatus('Please enter your email to continue.')
                setMissingEmail(null)
                return
            }

            setEmail(trimmedEmail)
            setStatus(null)
            setMissingEmail(null)
            setPassword('')

            try {
                setSubmitting(true)
                const recaptchaToken = await executeRecaptcha('check_email')
                const response = await authApi.checkEmailExists(
                    trimmedEmail,
                    recaptchaToken,
                )
                if (response.exists) {
                    setStep(LoginStep.ChooseMethod)
                } else {
                    setStep(LoginStep.NoAccount)
                }
            } catch (err: unknown) {
                const message =
                    err instanceof Error
                        ? err.message
                        : 'Unable to verify this email. Please try again.'
                setStatus(
                    message.replace(
                        /^There was an issue communicating with the Contxt server:\s*/,
                        '',
                    ),
                )
                setStep(LoginStep.Email)
            } finally {
                setSubmitting(false)
            }
        },
        [email],
    )

    const handleEditEmail = useCallback(() => {
        if (editEmailReturnPath) {
            navigate(editEmailReturnPath, { replace: true })
            return
        }
        setStep(LoginStep.Email)
        setPassword('')
        setStatus(null)
        setMissingEmail(null)
        setMagicLinkMessage(null)
    }, [editEmailReturnPath, navigate])

    const navigateToNewAccount = useCallback(
        (prefillEmail?: string) => {
            console.log('Navigating to new account creation', {
                prefillEmail,
                targetPath,
            })
            if (targetPath) {
                const signupDestination = resolveSignupDestination(targetPath)
                if (signupDestination) {
                    const params = new URLSearchParams()
                    if (prefillEmail) {
                        params.set(signupDestination.prefillKey, prefillEmail)
                    }
                    const query = params.toString()
                    navigate(
                        `${signupDestination.path}${query ? `?${query}` : ''}`,
                        { replace: true },
                    )
                    return
                }
            }

            const params = new URLSearchParams()
            if (prefillEmail) {
                params.set('email', prefillEmail)
                params.set('step', 'choose-method')
            }
            const query = params.toString()
            navigate(`${Pages.Signup}${query ? `?${query}` : ''}`)
        },
        [navigate, targetPath],
    )

    if (loading && !user) {
        return (
            <div
                className="min-h-screen flex items-center justify-center bg-background"
                style={speakToKitThemeVars}
            >
                <div className="h-12 w-12 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
        )
    }

    if (step === LoginStep.MagicSent) {
        return (
            <CandidateMagicLinkNotification
                email={email}
                subtitle={
                    magicLinkMessage ||
                    `We sent a magic link to ${email}. Use it to sign back in.`
                }
                onUseDifferentEmail={() => {
                    setStep(LoginStep.Email)
                    setMagicLinkMessage(null)
                }}
            />
        )
    }

    const mainLayoutClass =
        'flex-1 flex items-center justify-center px-4 py-6 sm:px-6 lg:px-8'

    return (
        <div
            className="min-h-screen bg-background text-foreground font-sans antialiased flex flex-col"
            style={speakToKitThemeVars}
        >
            <main className={mainLayoutClass}>
                <div className="w-full flex justify-center">
                    <div className="w-full max-w-lg mx-auto px-6 py-12 flex items-center justify-center rounded-3xl border border-border bg-card/30 shadow-[var(--shadow-soft)]">
                        <div className="space-y-6 text-center max-w-sm mx-auto w-full">
                            <div className="flex justify-center">
                                <SpeakToKitLogo
                                    to={Pages.CandidateLandingOption2}
                                />
                            </div>
                            {step === LoginStep.Email && (
                                <>
                                    <h2
                                        style={speakToKitSerifStyle}
                                        className="text-2xl font-semibold text-foreground"
                                    >
                                        Sign in to continue
                                    </h2>
                                    <p className="text-sm text-muted-foreground">
                                        Enter the email you used with Kit so we
                                        can resume your conversation.
                                    </p>
                                    <form
                                        className="space-y-4 text-left lg:text-center"
                                        onSubmit={handleEmailSubmit}
                                    >
                                        <div className="space-y-1 lg:text-left">
                                            <input
                                                id="candidate-login-email"
                                                type="email"
                                                className="w-full rounded-2xl border border-border bg-background/60 px-4 py-3 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                placeholder="Email"
                                                value={email}
                                                onChange={(e) =>
                                                    handleEmailChange(
                                                        e.target.value,
                                                    )
                                                }
                                                required
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            className={primaryButtonClasses}
                                            disabled={submitting}
                                        >
                                            Continue
                                        </button>
                                    </form>
                                </>
                            )}

                            {step === LoginStep.ChooseMethod && (
                                <div className="space-y-6 text-left lg:text-center">
                                    <div className="text-center bg-background/70 px-4 py-3 shadow-[var(--shadow-soft)]">
                                        <p className="text-sm font-medium text-foreground">
                                            Sign in with{' '}
                                            <strong>{email}</strong>
                                        </p>
                                    </div>

                                    <CandidateEmailLinkButton
                                        sendingLink={submitting}
                                        handleSendMagicLink={
                                            handleSelectMagicLink
                                        }
                                    />

                                    <div className="space-y-4 border-t border-border pt-4 text-left">
                                        <p className="text-sm font-semibold text-foreground">
                                            Or use your password
                                        </p>
                                        <form
                                            className="space-y-4"
                                            onSubmit={handlePasswordLogin}
                                        >
                                            <div className="space-y-1">
                                                <label
                                                    htmlFor="candidate-login-password"
                                                    className="text-sm font-medium text-foreground"
                                                >
                                                    Password
                                                </label>
                                                <div className="relative">
                                                    <input
                                                        id="candidate-login-password"
                                                        type={
                                                            showPassword
                                                                ? 'text'
                                                                : 'password'
                                                        }
                                                        className="w-full rounded-2xl border border-border bg-background/60 px-4 py-3 pr-12 text-base text-foreground shadow-inner focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                        placeholder="Password"
                                                        value={password}
                                                        onChange={(e) => {
                                                            setPassword(
                                                                e.target.value,
                                                            )
                                                            setStatus(null)
                                                        }}
                                                        required
                                                    />
                                                    <button
                                                        type="button"
                                                        className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                                                        onClick={() =>
                                                            setShowPassword(
                                                                (prev) => !prev,
                                                            )
                                                        }
                                                        aria-label={
                                                            showPassword
                                                                ? 'Hide password'
                                                                : 'Show password'
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={
                                                                showPassword
                                                                    ? faEyeSlash
                                                                    : faEye
                                                            }
                                                        />
                                                    </button>
                                                </div>
                                            </div>
                                            <button
                                                type="submit"
                                                className={
                                                    secondaryButtonClasses
                                                }
                                                disabled={submitting}
                                            >
                                                Log In
                                            </button>
                                        </form>
                                    </div>

                                    <div className="text-center">
                                        <button
                                            type="button"
                                            className="text-sm font-semibold text-primary hover:text-primary/80"
                                            onClick={handleEditEmail}
                                        >
                                            Use a different email
                                        </button>
                                    </div>
                                </div>
                            )}

                            {step === LoginStep.NoAccount && (
                                <div className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                        We can&apos;t find an account for{' '}
                                        <strong>{email}</strong>
                                    </p>
                                    <button
                                        type="button"
                                        className={primaryButtonClasses}
                                        disabled={submitting}
                                        onClick={() =>
                                            navigateToNewAccount(email)
                                        }
                                    >
                                        Create a new Contxt account
                                    </button>
                                    <button
                                        type="button"
                                        className="text-sm font-semibold text-primary hover:text-primary/80"
                                        onClick={handleEditEmail}
                                    >
                                        Use a different email
                                    </button>
                                </div>
                            )}

                            <div className="space-y-2 text-center">
                                {status && !missingEmail && (
                                    <p className="text-sm text-destructive whitespace-pre-wrap">
                                        {status}
                                    </p>
                                )}
                                {missingEmail && (
                                    <div className="space-y-2 rounded-2xl border border-border bg-background/70 p-4 text-sm text-muted-foreground">
                                        <p className="text-destructive">
                                            We can&apos;t find an account for{' '}
                                            <strong>{missingEmail}</strong>
                                        </p>
                                        <button
                                            type="button"
                                            className={secondaryButtonClasses}
                                            disabled={submitting}
                                            onClick={() =>
                                                navigateToNewAccount(
                                                    missingEmail,
                                                )
                                            }
                                        >
                                            Create a new Contxt account
                                        </button>
                                    </div>
                                )}
                                {error && (
                                    <p className="text-sm text-destructive whitespace-pre-wrap">
                                        {error}
                                    </p>
                                )}
                            </div>

                            {step === LoginStep.Email && (
                                <p className="text-sm text-muted-foreground">
                                    New to Contxt?{' '}
                                    {targetPath ? (
                                        <button
                                            type="button"
                                            className="font-semibold text-primary hover:text-primary/80 underline-offset-2"
                                            onClick={() =>
                                                navigateToNewAccount()
                                            }
                                        >
                                            Create a new account
                                        </button>
                                    ) : (
                                        <Link
                                            to={Pages.CandidateLandingOption2}
                                            className="font-semibold text-primary hover:text-primary/80"
                                        >
                                            Create a new account
                                        </Link>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    )
}

function extractMissingEmail(message: string): string | null {
    const match = message.match(/User with email "([^"]+)" not found/i)
    return match ? match[1] : null
}

function resolveSignupDestination(
    targetPath: string,
): { path: string; prefillKey: 'cand_email' | 'email' } | null {
    const [pathname] = targetPath.split('?')
    if (!pathname) return null

    const normalize = (value: string) => value.replace(/\/+$/, '')
    const cleanPath = normalize(pathname)

    if (cleanPath === normalize(Pages.CandidateApplyKit)) {
        return { path: Pages.CandidateApply, prefillKey: 'cand_email' }
    }

    const applyKitMatch = cleanPath.match(/^\/jobseeker\/apply\/([^/]+)\/kit$/i)
    if (applyKitMatch?.[1]) {
        return {
            path: `${Pages.CandidateApply}/${applyKitMatch[1]}`,
            prefillKey: 'cand_email',
        }
    }

    if (
        cleanPath === normalize(Pages.CandidateApply) ||
        cleanPath.startsWith(`${Pages.CandidateApply}/`)
    ) {
        return { path: cleanPath, prefillKey: 'cand_email' }
    }

    return null
}
